/**
 * 0.36.173 - PaginationContext + PaginationElement.
 *
 * Coordinates state between a Pagination element and a linked Table
 * element on the same rendered page. Each pagination references a
 * table by `table_id` (which is the Table's Element ID = its `name`).
 *
 * State flow:
 *  - PaginationProvider scans the structure at mount, building a
 *    rowCounts map: { tableName: numberOfRows }
 *  - States map { tableName: { currentPage, perPage } } is owned here.
 *    Default state initialized lazily on first read.
 *  - PaginationElement reads its linked rowCount, computes totalPages,
 *    auto-hides if rowCount <= defaultPerPage (canonical platform
 *    rule established in 0.36.311 / 0.36.312), otherwise renders
 *    PagePagination
 *    wired to dispatch updates back to context.
 *  - PageTable reads its own state (if linked) and slices rows.
 *
 * Used by both PageContentRenderer (public page render) and
 * AdminFormPreview (admin preview path). Two places wrap their output
 * in <PaginationProvider structure={...}>.
 */
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { getElementId } from '../utils/format';
import PagePagination from './PagePagination';
// 0.40.51 - Resolves an =lib(...) page template against the library
// cache for the citizen's language, and re-resolves when the cache
// lands. Same helper the header / footer / page titles use.
import { useResolvedTitle } from '../lib/resolveTitleText';
import { useLanguage } from '../context/LanguageContext';

export const PaginationContext = createContext(null);

/**
 * Walk a structure tree gathering tables (by name = Element ID) and
 * their row counts. Recurses into containers (column_group, line_group)
 * so a Table nested inside a row still gets counted.
 */
function scanTables(structure) {
  const map = {};
  const walk = (els) => {
    if (!Array.isArray(els)) return;
    for (const el of els) {
      if (!el || typeof el !== 'object') continue;
      if (el.type === 'table' && getElementId(el)) {
        map[getElementId(el)] = Array.isArray(el.rows) ? el.rows.length : 0;
      }
      if (Array.isArray(el.children)) walk(el.children);
      if (Array.isArray(el.columns)) {
        for (const col of el.columns) {
          if (Array.isArray(col?.children)) walk(col.children);
        }
      }
    }
  };
  walk(structure);
  return map;
}

/**
 * 0.36.218 — Walk a structure tree gathering Pagination elements
 * linked to a table (table_id set) and their per_page_default values.
 *
 * Used to pre-populate PaginationContext.states on mount so that
 * PageTable's first render already sees a non-null state for any
 * table linked to a Pagination element. Without this, there was a
 * race on first render:
 *
 *   - PaginationContext.states = {}  (empty)
 *   - PaginationElement reads `ctx.states[tableId] || {fallback...}`
 *     → renders dropdown showing per_page_default (e.g. 5)
 *   - PageTable reads `usePaginationStateForTable(name)` → returns
 *     null → shows ALL rows (no slice)
 *   - Result: dropdown says "5 per page" but table renders 20 rows.
 *
 * Sync only appeared after the first user interaction (which called
 * ctx.update and wrote a real entry). Pre-seeding here fixes that.
 */
function scanPaginationDefaults(structure) {
  const map = {};
  const walk = (els) => {
    if (!Array.isArray(els)) return;
    for (const el of els) {
      if (!el || typeof el !== 'object') continue;
      if (el.type === 'pagination' && el.table_id) {
        const perPage = Number(el.per_page_default) || 10;
        const currentPage = Number(el.current_page) || 1;
        map[el.table_id] = { currentPage, perPage };
      }
      // 0.36.669 — Table elements with built-in pagination seed
      // their OWN pagination defaults, self-linked via
      // getElementId(el) as the key. Same treatment as a standalone
      // Pagination linked to this table.
      /* 0.42.27 - A repeating display paginates exactly as a table
         does, so it is registered the same way. Without this its state
         is never created and usePaginationStateForTable returns null. */
      if ((el.type === 'table' || el.type === 'repeating_display')
          && el.pagination_enabled && getElementId(el)) {
        const perPage = Number(el.per_page_default) || 10;
        map[getElementId(el)] = { currentPage: 1, perPage };
      }
      if (Array.isArray(el.children)) walk(el.children);
      if (Array.isArray(el.columns)) {
        for (const col of el.columns) {
          if (Array.isArray(col?.children)) walk(col.children);
        }
      }
    }
  };
  walk(structure);
  return map;
}

/* 0.46.00 - `initialStates` / `onStatesChange`: a flow keeps each
   step's pages, so a step opened again shows the page the citizen
   left (page 7 of the results, not page 1). */
export function PaginationProvider({ structure, children, initialStates = null, onStatesChange = null }) {
  // tableRowCounts is derived from structure. It's used to compute
  // totalPages in PaginationElement.
  const tableRowCounts = useMemo(() => scanTables(structure), [structure]);

  // 0.36.218 — Per-table pagination states are now pre-populated
  // from the structure (any pagination elements with table_id +
  // per_page_default seed an entry). This eliminates the first-
  // render desync where PageTable showed all rows while the
  // Pagination dropdown showed a smaller per-page value. See
  // scanPaginationDefaults docstring above for details.
  const [states, setStates] = useState(() => ({
    ...scanPaginationDefaults(structure),
    ...(initialStates && typeof initialStates === 'object' ? initialStates : {}),
  }));
  useEffect(() => {
    if (typeof onStatesChange === 'function') onStatesChange(states);
  }, [states]);  // eslint-disable-line react-hooks/exhaustive-deps

  // 0.36.218 — When the structure changes (form editor live-edit:
  // pagination element added, removed, or its target table linked),
  // seed defaults for any newly-linked tables that don't have an
  // explicit entry yet. Existing entries (i.e. tables where the
  // user has already interacted) are preserved.
  useEffect(() => {
    const defaults = scanPaginationDefaults(structure);
    setStates((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const [tableId, def] of Object.entries(defaults)) {
        if (!prev[tableId]) {
          next[tableId] = def;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [structure]);

  const update = useCallback((tableId, patch) => {
    setStates((prev) => ({
      ...prev,
      [tableId]: {
        currentPage: 1,
        perPage: 10,
        ...(prev[tableId] || {}),
        ...patch,
      },
    }));
  }, []);

  // 0.36.671 — Runtime-known row counts. Library-backed tables
  // (source='library') don't have their row count in the structure
  // (rows are fetched from /api/form-libraries/{key}/rows), so
  // scanTables can't pick them up. LibraryBackedTable calls this
  // once its /meta answers. The dynamic map merges over the static
  // one when consumed via `tableRowCounts` — runtime values take
  // precedence for the same key.
  const [dynamicRowCounts, setDynamicRowCounts] = useState({});
  const reportRowCount = useCallback((tableId, count) => {
    setDynamicRowCounts((prev) => {
      if (prev[tableId] === count) return prev;
      return { ...prev, [tableId]: count };
    });
  }, []);

  const mergedRowCounts = useMemo(
    () => ({ ...tableRowCounts, ...dynamicRowCounts }),
    [tableRowCounts, dynamicRowCounts]
  );

  const value = useMemo(
    () => ({
      states,
      tableRowCounts: mergedRowCounts,
      update,
      reportRowCount,
    }),
    [states, mergedRowCounts, update, reportRowCount]
  );

  return (
    <PaginationContext.Provider value={value}>
      {children}
    </PaginationContext.Provider>
  );
}

/**
 * Hook for PageTable to read its own pagination state (if any).
 * Returns null if no provider above OR no state for this table.
 */
export function usePaginationStateForTable(tableName) {
  const ctx = useContext(PaginationContext);
  if (!ctx || !tableName) return null;
  return ctx.states[tableName] || null;
}

/**
 * Renderer wrapper used in place of inline PagePagination JSX in the
 * RENDERERS map. Handles both standalone (no table_id set) and
 * table-linked modes.
 */
/* 0.45.85 - After a page change, back to the top of what was paged.
 *
 * The paginator sits under a table or a repeating display, so after
 * Next the visitor was left at the bottom of the new page of rows or
 * cards. The element marks its top with data-pagination-anchor (its
 * Element ID); when that top is above the window, the page scrolls
 * back to it. Nothing moves when it is already in view. Works for
 * every source (API, library, collection, rows typed in), a table's own
 * paginator and a standalone Pagination element linked to it. */
export function scrollToPaginated(key) {
  if (!key || typeof document === 'undefined') return;
  const run = () => {
    let anchor = null;
    try {
      anchor = document.querySelector(`[data-pagination-anchor="${CSS.escape(String(key))}"]`);
    } catch { anchor = null; }
    if (!anchor) return;
    const top = anchor.getBoundingClientRect().top;
    if (top >= 0) return;
    const y = top + window.scrollY - 16;
    window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
  };
  if (typeof window !== 'undefined' && window.requestAnimationFrame) {
    window.requestAnimationFrame(run);
  } else run();
}

export function PaginationElement({ el }) {
  const ctx = useContext(PaginationContext);
  const tableId = el.table_id || '';
  // 0.40.51 - Page-indicator template, resolved for the citizen's
  // language so an =lib(...) value yields that language's wording.
  //
  // Called HERE, above every conditional return in this component:
  // there are two (standalone mode, and the row-count visibility
  // gate), and a hook after them would run on some renders and not
  // others.
  const { language } = useLanguage();
  const resolvedPageText = useResolvedTitle(el.page_text || '', language);
  // 0.40.67 - Same treatment as pageText: resolved through
  // useResolvedTitle so `=lib(...)` works and re-resolves when the
  // library cache warms or the language changes.
  const resolvedPrevLabel = useResolvedTitle(el.prev_label || '', language);
  const resolvedNextLabel = useResolvedTitle(el.next_label || '', language);
  // 0.45.88 - The "Show per page" label, resolved the same way, so an
  // =lib(...) value shows its wording rather than the formula.
  const resolvedPerPageLabel = useResolvedTitle(el.per_page_label ?? 'Show per page', language);

  // 0.44.22 - Moved above the standalone return: the auto-clamp effect
  // below is a hook, and after that return it ran on some renders and
  // not others (React error #300 when an element's table link changed).
  // The values are plain; with no table the effect does nothing.
  // Linked mode: pull row count from context, compute totalPages.
  const rowCount = ctx?.tableRowCounts?.[tableId] || 0;
  const defaultPerPage = Number(el.per_page_default) || 10;
  const state = ctx?.states?.[tableId] || {
    currentPage: 1,
    perPage: defaultPerPage,
  };
  const totalPages = Math.max(1, Math.ceil(rowCount / state.perPage));

  // 0.36.682 — Auto-clamp on row-count shrink. When rowCount drops
  // below (currentPage - 1) * perPage + 1 (e.g. filter/search
  // narrowed the results after the user paged deep), currentPage
  // becomes meaningless — the paginator would read "Page 5 of 2"
  // and the table would show zero rows. Detect and update the
  // context state to snap to the last valid page. Runs in useEffect
  // so it doesn't render-loop.
  /* 0.46.00 - Not before the count is known. A display reports its
     rows after it mounts, so on the first render the count read 0 and
     a page kept from an earlier visit (page 7, back in a flow) was
     snapped to page 1 before the rows arrived. */
  const countKnown = !!(ctx && ctx.tableRowCounts
    && Object.prototype.hasOwnProperty.call(ctx.tableRowCounts, tableId));
  useEffect(() => {
    if (!tableId || !ctx?.update || !countKnown) return;
    if (state.currentPage > totalPages) {
      ctx.update(tableId, { currentPage: totalPages });
    }
  }, [tableId, ctx, state.currentPage, totalPages, countKnown]);

  // Standalone mode (no link): render with element's own configured
  // values. This is the legacy/v0 behavior preserved for any existing
  // pagination elements that don't have a table_id set.
  if (!tableId) {
    return (
      <PagePagination
        totalPages={Number(el.total_pages) || 12}
        currentPage={Number(el.current_page) || 1}
        pageLabel={el.page_label ?? 'Page'}
        ofLabel={el.of_label || 'of'}
        pageText={resolvedPageText}
        {...(resolvedPrevLabel ? { prevLabel: resolvedPrevLabel } : {})}
        {...(resolvedNextLabel ? { nextLabel: resolvedNextLabel } : {})}
        showPerPage={!!el.show_per_page}
        perPageDefault={Number(el.per_page_default) || 10}
        perPageLabel={resolvedPerPageLabel}
        small={!!el.pagination_small}
      />
    );
  }


  // 0.36.312 — Visibility gate. Hide only if the row count is at or
  // below the *default* per-page (the canonical threshold), NOT when
  // the *current* per-page covers all rows. Without this, the user
  // could pick a per-page that hides the paginator and trap
  // themselves out of the per-page selector with no way back to a
  // smaller page size. Matches the platform-wide rule established
  // in 0.36.311 for AdminUsers / Translations / AuditLog /
  // UserDetail paginators.
  if (rowCount <= defaultPerPage) return null;

  return (
    <PagePagination
      totalPages={totalPages}
      currentPage={state.currentPage}
      pageLabel={el.page_label ?? 'Page'}
      ofLabel={el.of_label || 'of'}
      pageText={resolvedPageText}
      /* 0.45.88 - The Next / Previous wording was resolved here but
         passed only to an unlinked paginator, so a table's or a
         repeating display's own paginator always said "Next" and
         "Previous". */
      {...(resolvedPrevLabel ? { prevLabel: resolvedPrevLabel } : {})}
      {...(resolvedNextLabel ? { nextLabel: resolvedNextLabel } : {})}
      showPerPage={!!el.show_per_page}
      perPageDefault={state.perPage}
      perPageLabel={resolvedPerPageLabel}
      small={!!el.pagination_small}
      perPageOptions={el.per_page_options || null}
      onChange={(p) => { ctx?.update(tableId, { currentPage: p }); scrollToPaginated(tableId); }}
      onPerPageChange={(s) => {
        ctx?.update(tableId, { perPage: s, currentPage: 1 });
        scrollToPaginated(tableId);
      }}
    />
  );
}
